import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
  useTheme,
} from '@mui/material';
import React, { useCallback, useEffect, useMemo, useState } from 'react';

import { DialogCloseButton } from '../_controls_';

interface IProps {
  open: boolean;
  templateName: string;
  onClose: () => void;
  onSave: (name: string) => void;
  isLoading?: boolean;
  helperText?: string;
}

export const RenameClaimLinkTemplateDialog: React.FC<IProps> = ({
  open,
  templateName,
  onClose,
  onSave,
  isLoading = false,
}) => {
  const theme = useTheme();
  const [name, setName] = useState(templateName ?? '');

  const handleNameChange = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    setName(event.target.value);
  }, []);

  const handleSave = useCallback(() => {
    onSave((name ?? '').trim());
  }, [name, onSave]);

  useEffect(() => {
    if (open) {
      setName(templateName);
    }
  }, [open, templateName]);

  const isSaveDisabled = useMemo(() => {
    const trimmedName = (name ?? '').trim();
    return !trimmedName || trimmedName === (templateName ?? '').trim();
  }, [name, templateName]);

  return (
    <Dialog open={open} onClose={isLoading ? undefined : onClose} maxWidth="xs" fullWidth>
      <DialogTitle sx={{ pb: 1 }}>Edit template name</DialogTitle>
      <DialogCloseButton onClick={onClose} dataAction="close_rename_dialog" disabled={isLoading} />
      <DialogContent sx={{ pb: 2, pt: 3, position: 'relative' }}>
        {isLoading && (
          <Box
            sx={{
              position: 'absolute',
              inset: 0,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              zIndex: 1,
              bgcolor: 'background.paper',
              opacity: 0.8,
            }}
          >
            <CircularProgress size={32} />
          </Box>
        )}
        <TextField
          label="Template name"
          variant="outlined"
          fullWidth
          value={name}
          onChange={handleNameChange}
          disabled={isLoading}
          sx={{ mb: 2, mt: 1 }}
        />
      </DialogContent>
      <DialogActions sx={{ padding: 1, justifyContent: 'flex-end' }}>
        <Button variant="outlined" onClick={onClose} disabled={isLoading}>
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={handleSave}
          disabled={isSaveDisabled || isLoading}
          sx={{ color: theme.palette.common.white }}
        >
          Save
        </Button>
      </DialogActions>
    </Dialog>
  );
};
